/**
 * Cash & Bottom Line — what the airport earns and burns each day, where the
 * plan is missed, and the cost to cut or the revenue to recover, each with
 * the record it rests on and the risk of taking it.
 *
 * Revenue and operating cost come from the finance figures (entered, or the
 * demonstration's until they are); what work cost, what deferral costs and
 * what the capital projects have spent come from the platform. Every
 * candidate says which, and a figure on demonstration data is marked on the
 * tile, not in a footnote.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { RateCard, SourceRef } from "./decision";
import { commercial, financeRef, revenueBudget, revenueTotal, type Finance } from "./finance";
import { money } from "./intelligence";
import type { Kpi } from "./kpis";
import type { LiabilityRegister } from "./liability";
import type { ProjectRow } from "./projects";
import type { WorkOrder } from "./workOrders";
import { t } from "../i18n";

export interface CashTile {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  text: string;
  tone?: "good" | "bad" | "warn";
  assumed?: boolean;
  sources: SourceRef[];
}

export interface BridgeStep {
  label: string;
  amount: number;
  sources: SourceRef[];
}

export interface Candidate {
  key: string;
  kind: "cut" | "recover" | "avoid";
  area: string;
  label: string;
  /** a year's worth, in the site currency */
  perYear: number;
  working: string;
  /** what taking it puts at risk */
  risk: string;
  /** computed from records, or resting on a starting figure */
  basis: "computed" | "starting";
  sources: SourceRef[];
}

export interface CashInputs {
  now: Date;
  siteCode: string;
  finance: Finance;
  workOrders: WorkOrder[];
  liability: LiabilityRegister | null;
  projects: ProjectRow[];
  kpis: Kpi[];
  rateCard: RateCard;
}

export interface CashDesk {
  generatedAt: string;
  currency: string;
  basis: Finance["basis"];
  tiles: CashTile[];
  bridge: { budgetNet: number; actualNet: number; steps: BridgeStep[] };
  byArea: { area: string; actual: number; budget: number; variance: number; sources: SourceRef[] }[];
  workSpend: {
    planned: number;
    actual: number;
    orders: number;
    byDiscipline: { discipline: string; planned: number; actual: number; orders: number }[];
  };
  candidates: Candidate[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const r0 = (n: number) => Math.round(n);

export function buildCashDesk(inp: CashInputs): CashDesk {
  const f = inp.finance;
  const cur = f.currency;
  const days = Math.max(1, f.daysElapsed);
  const rev = revenueTotal(f);
  const revB = revenueBudget(f);
  const opex = f.opex.actual;
  const opexB = f.opex.budget;
  const net = rev - opex;
  const netB = revB - opexB;
  const perDay = (n: number) => n / days;
  const annual = (n: number) => (n / days) * 365;
  const fin = (what: string) => financeRef(f, what);
  const assumed = f.basis === "demo";
  const pax = f.passengers;

  /* the bridge from the plan to the actual bottom line */
  const steps: BridgeStep[] = [
    ...f.revenue.map((l) => ({
      label:
        l.actual >= l.budget
          ? t("{label} revenue over plan", { label: t(l.label) })
          : t("{label} revenue under plan", { label: t(l.label) }),
      amount: l.actual - l.budget,
      sources: [fin(t("{label} revenue", { label: t(l.label) }))],
    })),
    {
      label: opex <= opexB ? t("Operating cost under plan") : t("Operating cost over plan"),
      amount: opexB - opex,
      sources: [fin(t("operating cost"))],
    },
  ];

  /* what work cost on the platform */
  const done = inp.workOrders.filter((w) => w.costActual != null || w.costPlanned != null);
  const byDisc = new Map<string, { planned: number; actual: number; orders: number }>();
  for (const w of done) {
    const k = w.discipline ?? w.crew ?? t("unassigned");
    const cur_ = byDisc.get(k) ?? { planned: 0, actual: 0, orders: 0 };
    cur_.planned += w.costPlanned ?? 0;
    cur_.actual += w.costActual ?? 0;
    cur_.orders += 1;
    byDisc.set(k, cur_);
  }
  const workSpend = {
    planned: done.reduce((t, w) => t + (w.costPlanned ?? 0), 0),
    actual: done.reduce((t, w) => t + (w.costActual ?? 0), 0),
    orders: done.length,
    byDiscipline: [...byDisc.entries()]
      .map(([discipline, v]) => ({ discipline, ...v }))
      .sort((a, b) => b.actual - a.actual),
  };

  /* candidates */
  const candidates: Candidate[] = [];
  for (const l of commercial(f)) {
    if (l.actual < l.budget)
      candidates.push({
        key: `recover:${l.key}`,
        kind: "recover",
        area: t(l.label),
        label: t("{label} revenue back to plan", { label: t(l.label) }),
        perYear: r0(annual(l.budget - l.actual)),
        working: t("{amount} under plan over {days} days, annualised", {
          amount: money(l.budget - l.actual, cur),
          days,
        }),
        risk: t("a plan that was too high in the first place — check the plan before the tenants"),
        basis: assumed ? "starting" : "computed",
        sources: [fin(t("{label} revenue", { label: t(l.label) }))],
      });
  }
  for (const a of f.opexByArea ?? []) {
    if (a.actual > a.budget)
      candidates.push({
        key: `cut:${a.key}`,
        kind: "cut",
        area: t(a.label),
        label: t("{label} back to budget", { label: t(a.label) }),
        perYear: r0(annual(a.actual - a.budget)),
        working: t("{amount} over budget over {days} days, annualised", {
          amount: money(a.actual - a.budget, cur),
          days,
        }),
        risk:
          a.key === "maintenance"
            ? t("deferred maintenance escalates — the register below prices it")
            : a.key === "staff"
              ? t("service levels and the queue KPI")
              : a.key === "energy"
                ? t("none if it is waste; the green program's measures say where")
                : t("service levels"),
        basis: assumed ? "starting" : "computed",
        sources: [fin(t("{label} cost", { label: t(a.label) }))],
      });
  }
  const overruns = inp.workOrders.filter(
    (w) => w.costActual != null && w.costPlanned != null && w.costActual > w.costPlanned,
  );
  if (overruns.length) {
    const total = overruns.reduce((t, w) => t + (w.costActual! - w.costPlanned!), 0);
    candidates.push({
      key: "cut:overruns",
      kind: "cut",
      area: t("Maintenance"),
      label: t("Work-order overruns held to plan"),
      perYear: r0(annual(total)),
      working: t("{n} orders over their planned cost by {amount} in the period", {
        n: overruns.length,
        amount: money(total, cur),
      }),
      risk: t("a planned cost that was wrong, not work that was wasteful — read the orders"),
      basis: "computed",
      sources: overruns
        .slice(0, 6)
        .map((w) => ({ kind: "work_order", ref: w.number, label: w.title, href: `/work-orders/${w.id}` })),
    });
  }
  if (inp.liability) {
    const priced = inp.liability.register.filter(
      (r) => r.priced && r.costNow != null && r.costDeferred != null && r.costDeferred > r.costNow,
    );
    const esc = priced.reduce((t, r) => t + (r.costDeferred! - r.costNow!), 0);
    if (esc > 0)
      candidates.push({
        key: "avoid:escalation",
        kind: "avoid",
        area: t("Capital"),
        label: t("Do the priced deferred work now"),
        perYear: r0(esc / Math.max(1, ...priced.map((r) => r.deferYears ?? 1))),
        working: t("{n} priced candidates escalate by {amount} if deferred: {list}", {
          n: priced.length,
          amount: money(esc, cur),
          list: priced.map((r) => `${r.title} +${money(r.costDeferred! - r.costNow!, cur)}`).join("; "),
        }),
        risk: t("the cash now: {amount}", {
          amount: money(
            priced.reduce((sum, r) => sum + r.costNow!, 0),
            cur,
          ),
        }),
        basis: "computed",
        sources: priced.map((r) => ({ kind: "liability", ref: r.id, label: r.title, href: "/liability" })),
      });
  }
  const unaccounted = inp.kpis.find((k) => k.key === "unaccounted_energy_pct");
  const energy = f.opexByArea?.find((a) => a.key === "energy");
  if (unaccounted?.value != null && energy)
    candidates.push({
      /* its own key: "cut:energy" is the energy-over-budget line above, a different saving */
      key: "cut:energy-unaccounted",
      kind: "cut",
      area: t("Energy"),
      label: t("Unaccounted energy found and stopped"),
      perYear: r0(annual(energy.actual) * (unaccounted.value / 100)),
      working: t("{pct}% of energy unaccounted for × {amount} a year", {
        pct: unaccounted.value,
        amount: money(annual(energy.actual), cur),
      }),
      risk: t("none: it is energy nobody is using"),
      basis: assumed ? "starting" : "computed",
      sources: [
        { kind: "kpi", ref: unaccounted.key, label: unaccounted.label, href: "/executive-kpis" },
        fin(t("energy cost")),
      ],
    });
  candidates.sort((a, b) => b.perYear - a.perYear);

  /* capital */
  const capexBudget = f.capex.reduce((t, c) => t + c.budget, 0);
  const capexSpent = f.capex.reduce((t, c) => t + c.spent, 0);
  const platformCommitted = inp.projects.reduce((t, p) => t + (p.money.committedMinor ?? 0) / 100, 0);
  const platformSpent = inp.projects.reduce((t, p) => t + (p.money.spentMinor ?? 0) / 100, 0);

  const tiles: CashTile[] = [
    {
      key: "income",
      label: t("income a day"),
      value: perDay(rev),
      unit: cur,
      text: t("{amount} over {days} days, {period}", { amount: money(rev, cur), days, period: f.period }),
      assumed,
      tone: rev >= revB ? "good" : "warn",
      sources: [fin(t("revenue"))],
    },
    {
      key: "burn",
      label: t("burn a day"),
      value: perDay(opex),
      unit: cur,
      text: t("{amount} operating cost over {days} days", { amount: money(opex, cur), days }),
      assumed,
      tone: opex <= opexB ? "good" : "bad",
      sources: [fin(t("operating cost"))],
    },
    {
      key: "net",
      label: t("net a day"),
      value: perDay(net),
      unit: cur,
      text: t("{amount} against a plan of {plan}", { amount: money(net, cur), plan: money(netB, cur) }),
      assumed,
      tone: net >= netB ? "good" : "bad",
      sources: [fin(t("revenue and cost"))],
    },
    {
      key: "margin",
      label: t("operating margin"),
      value: rev ? (100 * net) / rev : null,
      unit: "%",
      text: t("plan {pct}%", { pct: revB ? Math.round((1000 * netB) / revB) / 10 : "—" }),
      assumed,
      tone: net / rev >= netB / revB ? "good" : "bad",
      sources: [fin(t("revenue and cost"))],
    },
    {
      key: "revPax",
      label: t("revenue a passenger"),
      value: pax ? rev / pax : null,
      unit: cur,
      text: pax ? t("{n} passengers in the period", { n: pax.toLocaleString("en") }) : t("no passenger count entered"),
      assumed,
      sources: [fin(t("revenue and passengers"))],
    },
    {
      key: "costPax",
      label: t("cost a passenger"),
      value: pax ? opex / pax : null,
      unit: cur,
      text: pax
        ? t("operating cost over {n} passengers", { n: pax.toLocaleString("en") })
        : t("no passenger count entered"),
      assumed,
      sources: [fin(t("cost and passengers"))],
    },
    {
      key: "capex",
      label: t("capital spent"),
      value: capexSpent,
      unit: cur,
      text: t(
        "of {budget} budgeted on {n} projects; the platform's projects show {spent} spent of {committed} committed",
        {
          budget: money(capexBudget, cur),
          n: f.capex.length,
          spent: money(platformSpent, cur),
          committed: money(platformCommitted, cur),
        },
      ),
      assumed,
      sources: [
        fin(t("capital")),
        ...inp.projects
          .slice(0, 4)
          .map((p) => ({ kind: "project" as const, ref: p.code, label: p.name, href: `/capital-projects/${p.id}` })),
      ],
    },
    {
      key: "candidates",
      label: t("on the table"),
      value: candidates.reduce((sum, c) => sum + c.perYear, 0),
      unit: cur,
      text: t("{n} candidates a year, cuts, recoveries and escalation avoided", { n: candidates.length }),
      tone: "good",
      sources: [],
    },
  ];
  const byArea = (f.opexByArea ?? []).map((a) => ({
    area: a.label,
    actual: a.actual,
    budget: a.budget,
    variance: a.actual - a.budget,
    sources: [fin(t("{label} cost", { label: t(a.label) }))],
  }));
  const biggest = candidates[0];
  const boardLine = [
    t(
      assumed
        ? "{site} earns {income} a day and burns {burn}: {net} net against a plan of {plan} — on the demonstration's figures."
        : "{site} earns {income} a day and burns {burn}: {net} net against a plan of {plan}.",
      {
        site: inp.siteCode,
        income: money(r0(perDay(rev)), cur),
        burn: money(r0(perDay(opex)), cur),
        net: money(r0(perDay(net)), cur),
        plan: money(r0(perDay(netB)), cur),
      },
    ),
    steps.length
      ? steps.some((s_) => s_.amount > 0)
        ? t("The plan is missed on {missed} and beaten on {beaten}.", {
            missed:
              steps
                .filter((s_) => s_.amount < 0)
                .map((s_) => s_.label.toLowerCase())
                .join(", ") || t("nothing"),
            beaten: steps
              .filter((s_) => s_.amount > 0)
              .map((s_) => s_.label.toLowerCase())
              .join(", "),
          })
        : t("The plan is missed on {missed}.", {
            missed:
              steps
                .filter((s_) => s_.amount < 0)
                .map((s_) => s_.label.toLowerCase())
                .join(", ") || t("nothing"),
          })
      : "",
    biggest
      ? t("The largest single move is {move}: {amount} a year, at the risk of {risk}.", {
          move: biggest.label.toLowerCase(),
          amount: money(biggest.perYear, cur),
          risk: biggest.risk,
        })
      : t("Nothing is on the table."),
  ].filter(Boolean);
  const material = materialForCash({
    tiles,
    bridge: { budgetNet: netB, actualNet: net, steps },
    byArea,
    workSpend,
    candidates,
    boardLine,
    currency: cur,
  } as CashDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    basis: f.basis,
    tiles,
    bridge: { budgetNet: netB, actualNet: net, steps },
    byArea,
    workSpend,
    candidates,
    boardLine,
    gaps: [
      ...(assumed
        ? [
            t(
              "revenue, operating cost and capital are the demonstration's figures until the finance system's are entered",
            ),
          ]
        : []),
      ...(pax ? [] : [t("no passenger count, so nothing is shown per passenger")]),
      ...(unaccounted ? [] : [t("no unaccounted-energy KPI on the platform, so energy waste is not priced")]),
    ],
    figures,
  };
}

export const CASH_MODEL_PROMPT = [
  "You are advising an airport's chief executive on its cash and bottom line from the material given and nothing else.",
  "You may explain, rank and argue; you must not state any number that is not in the material.",
  "Say when a figure is the demonstration's or entered rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForCash(
  d: Pick<CashDesk, "tiles" | "bridge" | "byArea" | "workSpend" | "candidates" | "boardLine" | "currency">,
): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(
      `Tile ${t.label}: ${t.value == null ? "n/a" : Math.round(t.value)} ${t.unit} — ${t.text}${t.assumed ? " [demo/entered]" : ""}`,
    );
  L.push(
    `Bridge: plan net ${Math.round(d.bridge.budgetNet)}, actual net ${Math.round(d.bridge.actualNet)}. ${d.bridge.steps.map((s) => `${s.label}: ${Math.round(s.amount)}`).join("; ")}`,
  );
  for (const a of d.byArea)
    L.push(
      `Area ${a.area}: actual ${Math.round(a.actual)}, budget ${Math.round(a.budget)}, variance ${Math.round(a.variance)}`,
    );
  L.push(
    `Work orders: ${d.workSpend.orders} priced, planned ${Math.round(d.workSpend.planned)}, actual ${Math.round(d.workSpend.actual)}. ${d.workSpend.byDiscipline.map((b) => `${b.discipline}: ${Math.round(b.actual)} actual / ${Math.round(b.planned)} planned on ${b.orders}`).join("; ")}`,
  );
  for (const c of d.candidates)
    L.push(
      `Candidate ${c.key} (${c.kind}, ${c.area}): ${c.label} — ${Math.round(c.perYear)} ${d.currency} a year. ${c.working}. Risk: ${c.risk}. Basis: ${c.basis}.`,
    );
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerCash(q: string, d: CashDesk): string | null {
  const t = q.toLowerCase();
  if (/burn|cost a day|per day|daily/.test(t))
    return d.tiles
      .filter((x) => ["income", "burn", "net"].includes(x.key))
      .map((x) => `${x.label}: ${money(Math.round(x.value ?? 0), d.currency)} — ${x.text}`)
      .join("\n");
  if (/cut|save|saving|recover|table|candidate/.test(t))
    return (
      d.candidates
        .map(
          (c, i) =>
            `${i + 1}. ${c.label}: ${money(c.perYear, d.currency)} a year (${c.kind}, ${c.basis}). ${c.working}. Risk: ${c.risk}.`,
        )
        .join("\n") || "Nothing is on the table."
    );
  if (/bridge|plan|miss|variance/.test(t))
    return `Plan net ${money(Math.round(d.bridge.budgetNet), d.currency)}, actual ${money(Math.round(d.bridge.actualNet), d.currency)}. ${d.bridge.steps.map((s) => `${s.label}: ${money(Math.round(s.amount), d.currency)}`).join("; ")}.`;
  if (/area|where|department/.test(t))
    return (
      d.byArea
        .map(
          (a) =>
            `${a.area}: ${money(a.actual, d.currency)} against ${money(a.budget, d.currency)} (${a.variance >= 0 ? "+" : ""}${money(a.variance, d.currency)})`,
        )
        .join("\n") || "No cost by area entered."
    );
  if (/work|maintenance spend|discipline/.test(t))
    return `${d.workSpend.orders} priced work orders: ${money(d.workSpend.actual, d.currency)} actual against ${money(d.workSpend.planned, d.currency)} planned. ${d.workSpend.byDiscipline.map((b) => `${b.discipline} ${money(b.actual, d.currency)}`).join("; ")}`;
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  return null;
}
